import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * Applies content-protection measures to LMS lesson content:
 *
 *   · disables text selection and right-click on wrapped content
 *   · blocks copy / cut keyboard shortcuts (Ctrl/Cmd + C, X, A, P, S)
 *   · adds a low-opacity diagonal watermark with the student's email
 *     repeated across the viewport, so any screenshot is trivially traceable
 *   · prevents `print` from producing a clean copy (hides page content in
 *     print CSS)
 *
 * This is friction, NOT DRM — a determined thief with a phone camera or
 * screen recorder can still capture content. But it makes casual copying
 * annoying and makes shared screenshots publicly traceable.
 *
 * Use by wrapping lesson content:
 *   <ProtectedContent>...</ProtectedContent>
 */
export default function ProtectedContent({ children, className = "" }) {
  const { user } = useAuth();
  const email = user?.email || "";

  useEffect(() => {
    // Block copy/save/print keyboard shortcuts on this route
    const blockKey = (e) => {
      const k = e.key?.toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      if (mod && ["c", "x", "a", "p", "s"].includes(k)) {
        e.preventDefault();
      }
    };
    // Block right-click context menu on the protected subtree only
    const blockContextMenu = (e) => {
      if (e.target?.closest?.("[data-protected-content]")) {
        e.preventDefault();
      }
    };
    // Prevent copy of selected text
    const blockCopy = (e) => {
      if (e.target?.closest?.("[data-protected-content]")) {
        e.preventDefault();
        e.clipboardData?.setData("text/plain", "");
      }
    };

    document.addEventListener("keydown", blockKey);
    document.addEventListener("contextmenu", blockContextMenu);
    document.addEventListener("copy", blockCopy);
    document.addEventListener("cut", blockCopy);

    return () => {
      document.removeEventListener("keydown", blockKey);
      document.removeEventListener("contextmenu", blockContextMenu);
      document.removeEventListener("copy", blockCopy);
      document.removeEventListener("cut", blockCopy);
    };
  }, []);

  const watermarkText = email ? `${email} · Aviana Aerial Yoga` : "Aviana Aerial Yoga · confidential";

  return (
    <div
      data-protected-content
      className={`relative ${className}`}
      style={{
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
      }}
    >
      {children}
      {/* Diagonal repeating watermark — very low opacity, non-blocking */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
          opacity: 0.06,
          transform: "rotate(-30deg)",
          transformOrigin: "top left",
          fontSize: "14px",
          fontFamily: "Georgia, serif",
          color: "#2b1f2b",
          lineHeight: "60px",
          whiteSpace: "nowrap",
        }}
        className="print:opacity-100"
        data-testid="content-watermark"
      >
        {Array.from({ length: 40 }).map((_, i) => (
          <div key={i} style={{ paddingLeft: `${(i % 4) * 200}px` }}>
            {watermarkText} · {watermarkText} · {watermarkText} · {watermarkText}
          </div>
        ))}
      </div>
      {/* Print-media suppression */}
      <style>{`
        @media print {
          [data-protected-content] { visibility: hidden !important; }
          [data-protected-content]::before {
            visibility: visible;
            content: "Aviana Aerial Yoga content is protected. Printing is disabled.";
            display: block;
            padding: 40px;
            font-family: Georgia, serif;
          }
        }
      `}</style>
    </div>
  );
}
